import apiClient from '@u/apiClient'
import { isCanceledRequest } from '@c/addressBook/directoryRequest'
import GetDirectoryListController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/GetDirectoryListController'
import GetDirectoryCountsController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/GetDirectoryCountsController'
import UpdateInternalContactController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/UpdateInternalContactController'
import PreviewMergeController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/PreviewMergeController'
import GetMergeCandidatesController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/GetMergeCandidatesController'
import ExecuteMergeController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/ExecuteMergeController'
import ArchiveDirectoryRecordsController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/ArchiveDirectoryRecordsController'
import UnarchiveDirectoryRecordsController from '@/wayfinder/actions/App/Http/Controllers/ContactDirectory/UnarchiveDirectoryRecordsController'

/**
 * The controller of the listing request currently in flight, or null.
 *
 * Module state rather than store state, and deliberately so. It is not data —
 * nothing renders it, no getter should reach it, and committing it would put a
 * live `AbortController` into a reactive tree that would then proxy it. It is
 * also not component state: the request belongs to the action, so the only
 * place that can know one listing has superseded another is the action itself,
 * and splitting that knowledge between the store and the page is how this kind
 * of fix ends up with two half-answers that disagree.
 *
 * One controller, not one per tab: only ever one listing is wanted at a time,
 * and the tab is a parameter of the request rather than a separate channel. A
 * map keyed by tab would keep a cancelled tab's controller alive for as long as
 * the page lived, for nothing.
 *
 * @type {AbortController|null}
 */
let listRequestController = null

/**
 * Give up on the listing in flight, if there is one.
 *
 * Clears the reference as well as aborting, so the next call does not abort a
 * controller that is already spent and so nothing holds a reference to a
 * request the user has moved on from.
 */
function abortListRequest() {
  listRequestController?.abort()
  listRequestController = null
}

function abortCandidateRequest() {
  candidateRequestController?.abort()
  candidateRequestController = null
}

function abortPreviewRequest() {
  previewRequestController?.abort()
  previewRequestController = null
}

/**
 * Whether the given listing request is still the one being waited on.
 *
 * False once a newer call has claimed the slot, and false once the page has
 * called the listing off — in both cases the answer, whatever it turns out to
 * be, belongs to a question nobody is asking any more.
 *
 * @param {AbortController} controller
 * @returns {boolean}
 */
function isCurrentListRequest(controller) {
  return listRequestController === controller
}

/**
 * The controller of the merge candidate search currently in flight, or null.
 *
 * A second, separate slot from `listRequestController`: the two searches are
 * unrelated requests that can be in flight together — the panel is open over
 * the listing, not instead of it — so sharing one controller would let
 * either one cancel the other by accident.
 *
 * @type {AbortController|null}
 */
let candidateRequestController = null

/**
 * The controller of the merge preview currently in flight, or null.
 *
 * A third, separate slot: a preview and a candidate search can be in flight
 * together (typing in the search box does not wait on the last principal
 * change to settle), so sharing either existing slot would let one cancel
 * the other by accident, exactly as `candidateRequestController` is kept
 * apart from `listRequestController`.
 *
 * @type {AbortController|null}
 */
let previewRequestController = null

export default {
  // region fetch
  /**
   * One page of one tab of the address book, superseding whatever was in flight.
   *
   * The response carries its own `meta.total`, so the badge of the tab being
   * looked at is correct without waiting for `fetchDirectoryCounts`.
   *
   * Every call cancels the previous one, because without that the address book
   * shows the wrong tab's rows: switching tabs faster than the slower tab
   * answers used to leave two requests racing, and the loser committing last
   * won the table. Ordering cannot be assumed — the tabs are backed by different
   * sources and do not take the same time to answer — so the fix is to make sure
   * there is only ever one answer worth having.
   *
   * It is guarded twice, because aborting is a request to stop and not a
   * guarantee of having stopped: a response already on the wire when `abort()`
   * is called can still arrive. So the signal keeps the connection from
   * finishing, and the identity check below keeps a response that finished
   * anyway from reaching the store. Guarding only the request would leave
   * exactly the bug this is fixing, in a narrower window.
   *
   * Cancellation is not a failure and is not reported as one. It resolves with
   * null rather than rejecting, so that a caller who does not expect it cannot
   * turn it into an unhandled rejection, and so that the one thing a caller has
   * to know — "this answer is not yours to act on" — is a value it can test
   * rather than an error class it has to recognise. A genuine failure still
   * rejects, exactly as before, and the HTTP interceptor still toasts it; it is
   * the interceptor that already keeps quiet about cancellations
   * (`apiClient.js:44`), so nothing the user sees changes.
   *
   * @param {object} context
   * @param {object} payload query parameters, as built by `buildListParams`
   * @returns {Promise<object|null>} the listing, or null when this call was
   *   superseded before it could commit
   */
  fetchDirectoryList({ commit }, payload) {
    abortListRequest()

    const controller = new AbortController()

    listRequestController = controller

    return apiClient
      .route(GetDirectoryListController.get(), {
        params: payload,
        signal: controller.signal
      })
      .then((response) => {
        // Not the request anyone is still waiting for: a newer call has taken
        // over, or the page has gone. Either way this answer describes a
        // question that is no longer being asked, and committing it would put
        // the tab the user just left back under the headers of the one they
        // asked for.
        if (!isCurrentListRequest(controller)) {
          return null
        }

        listRequestController = null
        commit('setDirectoryList', response.data)

        return response.data
      })
      .catch((reason) => {
        // Both halves of this are true of a cancellation, by construction —
        // `abortListRequest` clears the reference as it aborts — but they
        // answer different questions and neither implies the other in general.
        // A request can stop being the current one without having been
        // cancelled: aborting is a request to stop, so a call superseded a
        // moment before it was going to fail for real still reports that
        // failure. Its failure is as stale as its success would have been, and
        // is reported the same way, so that the newer call keeps sole charge of
        // what the page shows.
        const superseded = !isCurrentListRequest(controller)

        // Only release the slot when it is still ours: on a supersede the newer
        // call has already claimed it, and clearing it here would leave that one
        // with nothing able to cancel it.
        if (!superseded) {
          listRequestController = null
        }

        if (superseded || isCanceledRequest(reason)) {
          return null
        }

        throw reason
      })
  },

  /**
   * Call off the listing in flight, without starting another.
   *
   * For the page leaving: the request is on its way out, and letting it land
   * would commit into a store whose only reader has been destroyed, and settle
   * a promise whose `then` still holds a reference to that component. The
   * pending call answers null through the ordinary superseded path, so the
   * caller's own handler returns early and touches nothing.
   *
   * Safe to call when nothing is in flight.
   */
  cancelDirectoryList() {
    abortListRequest()
  },

  /**
   * Call off a candidate search in flight, without starting another —
   * same shape as cancelDirectoryList, for clearing the search box (nothing
   * to search for is not a new search) and for the panel closing.
   */
  cancelMergeCandidates() {
    abortCandidateRequest()
  },

  /**
   * Call off a merge preview in flight, without starting another.
   *
   * For the panel closing while a preview is still on its way: without this,
   * a preview that lands after the panel is gone still commits setMergeToken,
   * leaving a token in the store for a selection the screen no longer shows.
   */
  cancelMergePreview() {
    abortPreviewRequest()
  },

  /**
   * The row count of all four tabs, in one call.
   *
   * Takes no parameters: the route is unfiltered by design — its controller
   * accepts no request at all — so the badges report what each tab holds in
   * total and not what the active filters would leave. Passing the listing
   * filters here would be silently ignored rather than honoured.
   *
   * Deliberately *not* cancelled alongside the listing, for the same reason it
   * takes no parameters. Two counts calls in flight are the same question and
   * come back with the same body, covering all four tabs at once, so a late one
   * cannot overwrite a newer one with anything stale — the race the listing has
   * does not exist here. Cancelling would only add a way to fail: a tab change
   * landing between the call and its answer would abort the badges and leave
   * them reading as never fetched, with nothing to retry them. It is also the
   * shell that calls this, once (`TheAddressBookPage`), not the tab that
   * switches, so there is nothing to supersede in the first place.
   *
   * @param {object} context
   * @returns {Promise<object>}
   */
  fetchDirectoryCounts({ commit }) {
    return apiClient
      .route(GetDirectoryCountsController.get())
      .then((response) => {
        commit('setDirectoryCounts', response.data)

        return response.data
      })
  },
  // endregion

  // region update
  /**
   * Correct the contact details of one colleague from the Internal tab.
   *
   * Answers a message rather than the updated row, so there is nothing to commit
   * and the context is not destructured: the caller refreshes the listing to see
   * the change.
   *
   * @param {object} _context
   * @param {object} payload
   * @returns {Promise<object>}
   */
  updateInternalContact(_, payload) {
    return apiClient
      .route(
        UpdateInternalContactController.patch({
          internalContact: payload.userId
        }),
        payload.contact
      )
      .then((response) => response.data)
  },
  // endregion

  // region merge
  /**
   * What a merge of the given selection would do, and a token that freezes it.
   *
   * Cancelled exactly as the listing and the candidate search are: the panel
   * fires this on every change of principal, arbitration, or record set, and
   * a user who changes their mind faster than the API answers races it the
   * same way a fast typist races the candidate search — the call for the
   * *previous* principal can still be on the wire when the call for the
   * *new* one goes out, and network order does not follow call order. An
   * earlier revision of this docblock assumed calls could only ever resolve
   * in the order they were sent and called that assumption "in sequence";
   * it was wrong, and a slower answer to a stale principal was landing last
   * and putting that principal's arbitration back on screen.
   *
   * Resolves with null on a cancellation, exactly as fetchDirectoryList and
   * fetchMergeCandidates do, so the panel can tell "this preview is not
   * yours to act on" apart from a real answer without treating it as a
   * failure.
   *
   * Commits the token to the store rather than handing it back for the caller
   * to hold. A token kept in a component's data would survive a change of
   * arbitration the component forgot to clear it on, and executeMerge would
   * then fire a merge other than the one on screen; keeping the only copy in
   * the store, read by executeMerge alone, makes that impossible by
   * construction rather than by discipline. The token is committed only for
   * the preview that is still current, for the same reason: a superseded
   * preview's token describes a plan the screen no longer shows.
   *
   * @param {object} context
   * @param {{tab: string, principal_id: string, absorbed_ids: Array<string>, arbitration: Record<string, string>}} payload
   * @returns {Promise<object|null>} the preview, or null when this call was
   *   superseded before it could commit
   */
  fetchMergePreview({ commit }, payload) {
    previewRequestController?.abort()

    const controller = new AbortController()

    previewRequestController = controller

    return apiClient
      .route(PreviewMergeController.post(), payload, {
        signal: controller.signal
      })
      .then((response) => {
        if (previewRequestController !== controller) {
          return null
        }

        previewRequestController = null
        commit('setMergeToken', response.data.token)

        return response.data
      })
      .catch((reason) => {
        const superseded = previewRequestController !== controller

        if (!superseded) {
          previewRequestController = null
        }

        if (superseded || isCanceledRequest(reason)) {
          return null
        }

        throw reason
      })
  },

  /**
   * Search for a record to add to the merge selection, superseding whatever
   * search was in flight.
   *
   * The one merge request that cancels in flight, and for the listing's own
   * reason: the user can type faster than the API answers, and without
   * cancelling, a slower answer to an earlier keystroke can land after a
   * faster answer to a later one and show stale results.
   *
   * Resolves with null on a cancellation, exactly as fetchDirectoryList does,
   * so a caller that does not expect it cannot turn it into an unhandled
   * rejection.
   *
   * @param {object} _context
   * @param {{tab: string, search: string, exclude?: Array<string>, per_page?: number}} payload
   * @returns {Promise<Array<object>|null>}
   */
  fetchMergeCandidates(_, payload) {
    candidateRequestController?.abort()

    const controller = new AbortController()

    candidateRequestController = controller

    return apiClient
      .route(GetMergeCandidatesController.get(), {
        params: payload,
        signal: controller.signal
      })
      .then((response) => {
        if (candidateRequestController !== controller) {
          return null
        }

        candidateRequestController = null

        return response.data.data
      })
      .catch((reason) => {
        const superseded = candidateRequestController !== controller

        if (!superseded) {
          candidateRequestController = null
        }

        if (superseded || isCanceledRequest(reason)) {
          return null
        }

        throw reason
      })
  },

  /**
   * Run the merge frozen by the last preview.
   *
   * Not cancelled, and deliberately so: a preview abandoned mid-flight costs
   * nothing, but an execution cancelled mid-flight would leave the user
   * unsure whether the merge happened — the server will have done it either
   * way, since the abort only stops the client from waiting on the answer.
   *
   * Reads the token from the store rather than taking it as a parameter, for
   * the same reason fetchMergePreview commits it there instead of returning
   * it to a component. Clears it on success so a second click of an already
   * spent token fails the same way a stale one from a previous panel would —
   * with the 409 the API already answers, rather than by resending an id the
   * store still happened to remember.
   *
   * @param {object} context
   * @returns {Promise<object>} the outcome: principal_id, absorbed_ids, reassigned_total, forced_fields, warnings
   */
  executeMerge({ state, commit }) {
    if (state.mergeToken === null) {
      return Promise.reject(
        new Error('executeMerge called with no preview token in the store.')
      )
    }

    // skipValidationToast / skipConflictToast: the panel shows a 422 (e.g.
    // the role-conflict check) and the 409s (stale token, drift) inline
    // instead — a global toast alone was gone before most users read it, and
    // did not reappear on an identical retry (many toast implementations
    // dedupe identical consecutive messages).
    return apiClient
      .route(
        ExecuteMergeController.post(),
        { token: state.mergeToken },
        { skipValidationToast: true, skipConflictToast: true }
      )
      .then((response) => {
        commit('setMergeToken', null)

        return response.data
      })
  },
  // endregion

  // region archive
  /**
   * Archive every record of a selection: soft delete plus archived_at
   * (`ArchiveDirectoryRecordsController`), on the tab and the ids the
   * toolbar's selection held.
   *
   * Not cancelled, for the same reason executeMerge is not: an archive
   * abandoned mid-flight would leave the user unsure whether it happened —
   * the server will have done it either way.
   *
   * @param {object} _context
   * @param {{tab: string, ids: Array<string>}} payload
   * @returns {Promise<object>}
   */
  archiveDirectoryRecords(_, payload) {
    return apiClient
      .route(ArchiveDirectoryRecordsController.post(), payload)
      .then((response) => response.data)
  },
  /**
   * Restore every record of a selection made on the archived_only listing:
   * the inverse of archiveDirectoryRecords, hitting
   * UnarchiveDirectoryRecordsController.
   *
   * Not cancelled, for the same reason archiveDirectoryRecords is not.
   *
   * @param {object} _context
   * @param {{tab: string, ids: Array<string>}} payload
   * @returns {Promise<object>}
   */
  unarchiveDirectoryRecords(_, payload) {
    return apiClient
      .route(UnarchiveDirectoryRecordsController.post(), payload)
      .then((response) => response.data)
  }
  // endregion
}
